import i18n, { type BackendModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { SUPPORTED_LANGUAGES } from './languages';

export { SUPPORTED_LANGUAGES };

type LocaleModule = { default: Record<string, unknown> };

// French ships in the entry script: it is the fallback of every missing key and most clubs' language.
const frenchModules = import.meta.glob<LocaleModule>('./locales/fr/*.json', { eager: true });
// The other six load on demand (VOL-343): bundled eagerly, the seven weighed 1.8 MB of a 2.3 MB entry
// script, parsed on every cold start of the app. vite.config.ts groups each language in one chunk.
const lazyModules = import.meta.glob<LocaleModule>(['./locales/*/*.json', '!./locales/fr/*.json']);

const french: Record<string, Record<string, unknown>> = {};
for (const [path, module] of Object.entries(frenchModules)) {
  const namespace = path.match(/\/([^/]+)\.json$/)?.[1];
  if (namespace) french[namespace] = module.default;
}

/** French is the reference: every language holds the same namespaces (i18n.test.ts). */
export const NAMESPACES = Object.keys(french).sort();

// The first render waits for the language: a request that hangs must not leave a blank screen.
export const LOCALE_LOAD_TIMEOUT_MS = 3000;

export function createLocaleBackend(
  loaders: Record<string, () => Promise<LocaleModule>>,
  timeoutMs = LOCALE_LOAD_TIMEOUT_MS,
): BackendModule {
  return {
    type: 'backend',
    init() {},
    read(language, namespace, callback) {
      const load = loaders[`./locales/${language}/${namespace}.json`];
      if (!load) {
        callback(null, {});
        return;
      }
      let settled = false;
      // `false`: no retry. Offline, or past the timeout, French shows at once instead of after
      // i18next's growing retry delays.
      const settle = (error: Error | null, data: Record<string, unknown> | false) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        callback(error, data);
      };
      const timer = setTimeout(
        () => settle(new Error(`${language}/${namespace} took over ${timeoutMs} ms`), false),
        timeoutMs,
      );
      load().then(
        (module) => settle(null, module.default),
        (error: unknown) => settle(error instanceof Error ? error : new Error(String(error)), false),
      );
    },
  };
}

// index.html ships lang="fr": screen readers would read Polish with French phonetics, and browsers
// would offer to translate a page already in the reader's language. No document in the PDF
// renderers and service worker tests, which run under Node.
i18n.on('languageChanged', (language) => {
  if (typeof document !== 'undefined') document.documentElement.lang = language;
});

/** Resolves once the detected language is loaded, French at once; never rejects. */
export const i18nReady = i18n
  .use(createLocaleBackend(lazyModules))
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { fr: french },
    // French is bundled, the others come from the backend; changeLanguage waits for them.
    partialBundledLanguages: true,
    fallbackLng: 'fr',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false,
    },
    defaultNS: 'common',
    ns: NAMESPACES,
  });

export { i18n };
